import { useState } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";

const GET_CONTRACTS = gql`
  query MissionAdminContracts($filter: MissionAdminContractFilter) {
    missionAdminContracts(filter: $filter) {
      id
      title
      brand {
        id
        name
      }
      totalBudget
      startAt
      endAt
      status
      campaigns {
        id
        productName
        brand {
          id
          name
        }
        channel
        createdAt
      }
    }
  }
`;

const GET_CAMPAIGNS_WITHOUT_CONTRACT = gql`
  query MissionAdminCampaignsWithoutContract(
    $filter: MissionAdminCampaignFilter
  ) {
    missionAdminCampaignsWithoutContract(filter: $filter) {
      id
      productName
      brand {
        id
        name
      }
      channel
      createdAt
    }
  }
`;

const CONNECT_CAMPAIGNS_TO_CONTRACT = gql`
  mutation MissionAdminConnectCampaignsToContract(
    $campaignIds: [Int!]!
    $contractId: Int!
  ) {
    missionAdminConnectCampaignsToContract(
      campaignIds: $campaignIds
      contractId: $contractId
    ) {
      ok
      error
    }
  }
`;

const DISCONNECT_CAMPAIGNS_FROM_CONTRACT = gql`
  mutation MissionAdminDisconnectCampaignsFromContract($campaignIds: [Int!]!) {
    missionAdminDisconnectCampaignsFromContract(campaignIds: $campaignIds) {
      ok
      error
    }
  }
`;

const MissionAdminContractsConnect = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"connect" | "disconnect">(
    "connect"
  );
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    null
  );
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);

  const { data: contractsData, refetch: refetchContracts } = useQuery(
    GET_CONTRACTS,
    {
      variables: {
        filter: { status: "ACTIVE" },
      },
    }
  );

  // 선택된 계약의 브랜드 ID 가져오기
  const selectedContract = contractsData?.missionAdminContracts?.find(
    (c: any) => c.id === selectedContractId
  );
  const selectedContractBrandId = selectedContract?.brand?.id;

  const { data: campaignsData, refetch: refetchCampaigns } = useQuery(
    GET_CAMPAIGNS_WITHOUT_CONTRACT,
    {
      variables: {
        filter: selectedContractBrandId
          ? { brandId: selectedContractBrandId }
          : {},
      },
      skip: !selectedContractId || activeTab !== "connect",
    }
  );

  const [connectCampaigns, { loading: connecting }] = useMutation(
    CONNECT_CAMPAIGNS_TO_CONTRACT,
    {
      onCompleted: () => {
        alert(
          `${selectedCampaignIds.length}개의 캠페인이 계약에 연결되었습니다.`
        );
        setSelectedCampaignIds([]);
        refetchContracts();
        refetchCampaigns();
      },
      onError: (error) => {
        alert(`에러: ${error.message}`);
      },
    }
  );

  const [disconnectCampaigns, { loading: disconnecting }] = useMutation(
    DISCONNECT_CAMPAIGNS_FROM_CONTRACT,
    {
      onCompleted: () => {
        alert(
          `${selectedCampaignIds.length}개의 캠페인이 계약에서 해제되었습니다.`
        );
        setSelectedCampaignIds([]);
        refetchContracts();
      },
      onError: (error) => {
        alert(`에러: ${error.message}`);
      },
    }
  );

  const handleCampaignToggle = (campaignId: number) => {
    setSelectedCampaignIds((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleConnect = async () => {
    if (selectedCampaignIds.length === 0 || !selectedContractId) {
      alert("캠페인과 계약을 모두 선택해주세요.");
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedCampaignIds.length}개의 캠페인을 계약에 연결하시겠습니까?`
      )
    ) {
      return;
    }

    try {
      await connectCampaigns({
        variables: {
          campaignIds: selectedCampaignIds,
          contractId: selectedContractId,
        },
      });
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  const handleDisconnect = async () => {
    if (selectedCampaignIds.length === 0) {
      alert("해제할 캠페인을 선택해주세요.");
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedCampaignIds.length}개의 캠페인을 계약에서 해제하시겠습니까?`
      )
    ) {
      return;
    }

    try {
      await disconnectCampaigns({
        variables: {
          campaignIds: selectedCampaignIds,
        },
      });
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  const filteredContracts = contractsData?.missionAdminContracts || [];
  const filteredCampaigns =
    campaignsData?.missionAdminCampaignsWithoutContract || [];
  const connectedCampaigns = selectedContract?.campaigns || [];

  const handleSelectAll = (campaigns: any[]) => {
    if (selectedCampaignIds.length === campaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(campaigns.map((c: any) => c.id));
    }
  };

  const handleTabChange = (tab: "connect" | "disconnect") => {
    setActiveTab(tab);
    setSelectedCampaignIds([]);
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">캠페인-계약 연결/해제</h1>
        <button
          onClick={() => navigate("/ops/mission/contracts/create")}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          계약 생성
        </button>
      </div>

      {/* 탭 */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => handleTabChange("connect")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "connect"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            연결
          </button>
          <button
            onClick={() => handleTabChange("disconnect")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "disconnect"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            해제
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* 계약 목록 */}
        <div>
          <h2 className="text-lg font-semibold mb-4">계약 목록 (ACTIVE)</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
            {filteredContracts.length === 0 ? (
              <p className="text-gray-500 text-sm">계약이 없습니다.</p>
            ) : (
              filteredContracts.map((contract: any) => (
                <div
                  key={contract.id}
                  className={`p-3 border rounded-md transition-colors ${
                    selectedContractId === contract.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div
                    onClick={() => {
                      setSelectedContractId(contract.id);
                      setSelectedCampaignIds([]);
                    }}
                    className="cursor-pointer"
                  >
                    <div className="font-semibold">{contract.title}</div>
                    <div className="text-sm text-gray-600">
                      {contract.brand.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      예산: {contract.totalBudget.toLocaleString()}원
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(contract.startAt).toLocaleDateString()} ~{" "}
                      {new Date(contract.endAt).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      연결된 캠페인: {contract.campaigns.length}개
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ops/mission/contracts/${contract.id}/edit`);
                      }}
                      className="w-full px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      수정
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 캠페인 목록 */}
        <div>
          {activeTab === "connect" ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  {selectedContractId
                    ? `${selectedContract?.brand?.name} 브랜드의 계약 없는 캠페인`
                    : "계약을 선택하면 해당 브랜드의 캠페인이 표시됩니다"}
                </h2>
                {filteredCampaigns.length > 0 && (
                  <button
                    onClick={() => handleSelectAll(filteredCampaigns)}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {selectedCampaignIds.length === filteredCampaigns.length
                      ? "전체 해제"
                      : "전체 선택"}
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
                {!selectedContractId ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    왼쪽에서 계약을 선택해주세요.
                  </p>
                ) : filteredCampaigns.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    {selectedContract?.brand?.name} 브랜드에 계약이 없는
                    캠페인이 없습니다.
                  </p>
                ) : (
                  filteredCampaigns.map((campaign: any) => (
                    <div
                      key={campaign.id}
                      onClick={() => handleCampaignToggle(campaign.id)}
                      className={`p-3 border rounded-md cursor-pointer transition-colors flex items-start gap-3 ${
                        selectedCampaignIds.includes(campaign.id)
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCampaignIds.includes(campaign.id)}
                        onChange={() => handleCampaignToggle(campaign.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">
                          {campaign.productName}
                        </div>
                        <div className="text-sm text-gray-600">
                          {campaign.brand.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          채널: {campaign.channel}
                        </div>
                        <div className="text-xs text-gray-400">
                          생성일:{" "}
                          {new Date(campaign.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  {selectedContractId
                    ? `${selectedContract?.brand?.name} 브랜드의 연결된 캠페인`
                    : "계약을 선택하면 연결된 캠페인이 표시됩니다"}
                </h2>
                {connectedCampaigns.length > 0 && (
                  <button
                    onClick={() => handleSelectAll(connectedCampaigns)}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {selectedCampaignIds.length === connectedCampaigns.length
                      ? "전체 해제"
                      : "전체 선택"}
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
                {!selectedContractId ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    왼쪽에서 계약을 선택해주세요.
                  </p>
                ) : connectedCampaigns.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    {selectedContract?.brand?.name} 브랜드에 연결된 캠페인이
                    없습니다.
                  </p>
                ) : (
                  connectedCampaigns.map((campaign: any) => (
                    <div
                      key={campaign.id}
                      onClick={() => handleCampaignToggle(campaign.id)}
                      className={`p-3 border rounded-md cursor-pointer transition-colors flex items-start gap-3 ${
                        selectedCampaignIds.includes(campaign.id)
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCampaignIds.includes(campaign.id)}
                        onChange={() => handleCampaignToggle(campaign.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">
                          {campaign.productName}
                        </div>
                        <div className="text-sm text-gray-600">
                          {campaign.brand.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          채널: {campaign.channel}
                        </div>
                        <div className="text-xs text-gray-400">
                          생성일:{" "}
                          {new Date(campaign.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 연결 버튼 */}
      {activeTab === "connect" &&
        selectedCampaignIds.length > 0 &&
        selectedContractId && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="mb-4">
              <p className="text-sm text-gray-700">
                선택된 캠페인:{" "}
                <span className="font-semibold">
                  {selectedCampaignIds.length}개
                </span>
              </p>
              <p className="text-sm text-gray-700 mt-1">
                선택된 계약:{" "}
                <span className="font-semibold">
                  {
                    filteredContracts.find(
                      (c: any) => c.id === selectedContractId
                    )?.title
                  }
                </span>
              </p>
              {selectedCampaignIds.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  선택된 캠페인:{" "}
                  {selectedCampaignIds
                    .map(
                      (id) =>
                        filteredCampaigns.find((c: any) => c.id === id)
                          ?.productName
                    )
                    .join(", ")}
                </div>
              )}
            </div>
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              {connecting
                ? "연결 중..."
                : `${selectedCampaignIds.length}개 연결하기`}
            </button>
          </div>
        )}

      {/* 해제 버튼 */}
      {activeTab === "disconnect" && selectedCampaignIds.length > 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="mb-4">
            <p className="text-sm text-gray-700">
              해제할 캠페인:{" "}
              <span className="font-semibold">
                {selectedCampaignIds.length}개
              </span>
            </p>
            {selectedContractId && (
              <p className="text-sm text-gray-700 mt-1">
                선택된 계약:{" "}
                <span className="font-semibold">
                  {
                    filteredContracts.find(
                      (c: any) => c.id === selectedContractId
                    )?.title
                  }
                </span>
              </p>
            )}
            {selectedCampaignIds.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                선택된 캠페인:{" "}
                {selectedCampaignIds
                  .map(
                    (id) =>
                      connectedCampaigns.find((c: any) => c.id === id)
                        ?.productName
                  )
                  .join(", ")}
              </div>
            )}
          </div>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
          >
            {disconnecting
              ? "해제 중..."
              : `${selectedCampaignIds.length}개 해제하기`}
          </button>
        </div>
      )}
    </div>
  );
};

export default MissionAdminContractsConnect;
